import { getBackendUrl } from "@/lib/backend";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut, RefreshCw, Trash2, Search, Mail, Building2, Users, CalendarDays,
  Check, X as XIcon, Download, Loader2, TrendingUp, TrendingDown, Minus, Sparkles, IndianRupee,
  AlertTriangle, ScrollText,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import LuxuryConfirmModal from "@/components/LuxuryConfirmModal";
import AdminCoupons from "@/components/admin/AdminCoupons";
import AdminBroadcasts from "@/components/admin/AdminBroadcasts";
import AdminCostDashboard from "@/components/admin/AdminCostDashboard";
import AdminBillingLeaks from "@/components/admin/AdminBillingLeaks";
import AdminCouponUsage from "@/components/admin/AdminCouponUsage";
import AdminVendors from "@/components/admin/AdminVendors";
import AdminAuditLog from "@/components/admin/AdminAuditLog";
import { ADMIN_ROUTE } from "@/lib/admin";
import { toCSV, downloadCSV, todayStamp } from "@/lib/csv";
import { computeWaitlistKpis } from "@/lib/kpiDates";

const API = `${getBackendUrl()}/api`;

function DeltaPill({ delta, label }) {
  if (delta === null || delta === undefined) {
    return <span className="text-[10px] text-white/35">{label}</span>;
  }
  if (delta === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-white/45">
        <Minus className="w-3 h-3" /> 0 {label}
      </span>
    );
  }
  const up = delta > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const color = up ? "text-emerald-300" : "text-rose-300";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] ${color}`}>
      <Icon className="w-3 h-3" />
      {up ? "+" : ""}{delta} {label}
    </span>
  );
}

function KpiCard({ title, value, delta, label, accent = false, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border p-4 sm:p-5 transition ${
        accent
          ? "border-[#D4AF37]/35 bg-gradient-to-br from-[#D4AF37]/8 via-[#1a1306] to-[#0E0E0E]"
          : "border-white/8 bg-[#0E0E0E]"
      }`}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">{title}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <span
          className={`font-display tracking-tight ${
            accent ? "text-[#D4AF37]" : "text-white"
          } text-3xl sm:text-4xl leading-none`}
        >
          {value}
        </span>
      </div>
      <div className="mt-2">
        <DeltaPill delta={delta} label={label} />
      </div>
    </div>
  );
}

const WAITLIST_COLS = [
  { label: "Name",      value: (e) => e.name },
  { label: "Email",     value: (e) => e.email },
  { label: "Company",   value: (e) => e.company || "" },
  { label: "Role",      value: (e) => e.role || "" },
  { label: "Message",   value: (e) => e.message || "" },
  { label: "Submitted", value: (e) => e.created_at || "" },
];
const BOOKING_COLS = [
  { label: "Name",      value: (b) => b.name },
  { label: "Email",     value: (b) => b.email },
  { label: "Company",   value: (b) => b.company || "" },
  { label: "Date",      value: (b) => b.date },
  { label: "Time",      value: (b) => b.time_slot },
  { label: "Status",    value: (b) => b.status || "pending" },
  { label: "Notes",     value: (b) => b.notes || "" },
  { label: "Submitted", value: (b) => b.created_at || "" },
];

const STATUS_PILL = {
  pending:   { cls: "border-white/15 text-white/55",         label: "Pending" },
  confirmed: { cls: "border-emerald-400/40 text-emerald-300", label: "Confirmed" },
  declined:  { cls: "border-rose-400/40 text-rose-300",       label: "Declined" },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("users");
  const [entries, setEntries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersByProvider, setUsersByProvider] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [providerFilter, setProviderFilter] = useState("all");
  const [deleteCtx, setDeleteCtx] = useState(null); // { kind, id, label }
  const [statusBusyId, setStatusBusyId] = useState(null);

  // Admin auth is now a server-set httpOnly cookie. We can't read the cookie
  // from JS by design (XSS protection). On mount we ping /admin/session to
  // confirm the cookie is valid; if not, we bounce to /admin login.
  // Empty header — kept for prop compatibility with child components that
  // still spread `headers={...authHeader}` into axios. The real auth rides
  // via the httpOnly cookie + withCredentials.
  const authHeader = useMemo(() => ({}), []);

  const unauthorized = useCallback(() => {
    try { localStorage.removeItem("facetra_admin_token"); } catch (e) { console.error("Could not clear legacy token", e); }
    navigate(ADMIN_ROUTE);
  }, [navigate]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [w, b, u] = await Promise.all([
        axios.get(`${API}/admin/waitlist`),
        axios.get(`${API}/admin/demo-bookings`),
        axios.get(`${API}/admin/users`),
      ]);
      setEntries(w.data.entries || []);
      setBookings(b.data.entries || []);
      setUsers(u.data.users || []);
      setUsersByProvider(u.data.by_provider || {});
    } catch (err) {
      if (err?.response?.status === 401) return unauthorized();
      toast.error("Failed to load data");
      console.error("admin loadAll failed", err);
    } finally {
      setLoading(false);
    }
  }, [unauthorized]);

  useEffect(() => {
    axios
      .get(`${API}/admin/session`)
      .then(() => {
        loadAll();
      })
      .catch(() => unauthorized());
  }, [loadAll, unauthorized]);

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch (e) {
      console.error("admin logout request failed", e);
    }
    try { localStorage.removeItem("facetra_admin_token"); } catch (e) { console.error("Could not clear legacy token", e); }
    navigate(ADMIN_ROUTE);
  };

  const confirmDelete = async () => {
    if (!deleteCtx) return;
    const { kind, id } = deleteCtx;
    const url =
      kind === "waitlist"
        ? `${API}/admin/waitlist/${id}`
        : kind === "user"
        ? `${API}/admin/users/${id}`
        : `${API}/admin/demo-bookings/${id}`;
    try {
      await axios.delete(url);
      if (kind === "waitlist") setEntries(entries.filter((e) => e.id !== id));
      else if (kind === "user") setUsers(users.filter((u) => u.user_id !== id));
      else setBookings(bookings.filter((e) => e.id !== id));
      toast.success("Deleted");
    } catch (err) {
      console.error("admin delete failed", err);
      toast.error("Could not delete");
    } finally {
      setDeleteCtx(null);
    }
  };

  const updateBookingStatus = async (booking, newStatus) => {
    if ((booking.status || "pending") === newStatus) {
      toast.info(`Already ${newStatus}.`);
      return;
    }
    setStatusBusyId(booking.id);
    try {
      const res = await axios.patch(
        `${API}/admin/demo-bookings/${booking.id}`,
        { status: newStatus },
        { headers: authHeader }
      );
      setBookings((bs) =>
        bs.map((b) => (b.id === booking.id ? { ...b, status: res.data.status } : b))
      );
      const verb = newStatus === "confirmed" ? "Confirmed" : "Declined";
      toast.success(`${verb} · email sent to ${booking.email}`);
    } catch (err) {
      if (err?.response?.status === 401) return unauthorized();
      toast.error("Could not update status");
    } finally {
      setStatusBusyId(null);
    }
  };

  const exportCSV = (kind) => {
    if (kind === "waitlist") {
      if (filteredWaitlist.length === 0) {
        toast.info("Nothing to export.");
        return;
      }
      downloadCSV(
        `facetra_waitlist_${todayStamp()}.csv`,
        toCSV(filteredWaitlist, WAITLIST_COLS),
      );
      toast.success(`Exported ${filteredWaitlist.length} waitlist row(s).`);
    } else if (kind === "users") {
      if (filteredUsers.length === 0) {
        toast.info("Nothing to export.");
        return;
      }
      const USER_COLS = [
        "email", "name", "provider", "plan", "credits",
        "email_verified", "on_waitlist", "workspace_id",
        "created_at", "last_login", "user_id",
      ];
      downloadCSV(
        `facetra_users_${todayStamp()}.csv`,
        toCSV(filteredUsers, USER_COLS),
      );
      toast.success(`Exported ${filteredUsers.length} user row(s).`);
    } else {
      if (filteredBookings.length === 0) {
        toast.info("Nothing to export.");
        return;
      }
      downloadCSV(
        `facetra_bookings_${todayStamp()}.csv`,
        toCSV(filteredBookings, BOOKING_COLS),
      );
      toast.success(`Exported ${filteredBookings.length} booking row(s).`);
    }
  };

  const filteredWaitlist = entries.filter((e) => {
    const q = query.toLowerCase();
    return !q || [e.name, e.email, e.company].some((v) => (v || "").toLowerCase().includes(q));
  });

  const filteredBookings = bookings.filter((b) => {
    const q = query.toLowerCase();
    return !q || [b.name, b.email, b.company].some((v) => (v || "").toLowerCase().includes(q));
  });

  const filteredUsers = users.filter((u) => {
    if (providerFilter !== "all" && u.provider !== providerFilter) return false;
    const q = query.toLowerCase();
    if (!q) return true;
    return [u.name, u.email, u.user_id].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#0A0A0A] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center">
              <span className="font-display text-black text-xs font-bold">F</span>
            </div>
            <span className="font-display text-lg tracking-tight">Facetra · Admin</span>
          </Link>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="text-xs text-white/60 hover:text-[#D4AF37] inline-flex items-center gap-1.5 transition"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <TabsList className="bg-[#141414] border border-white/5 p-1 rounded-xl h-auto">
              <TabsTrigger
                value="users"
                data-testid="tab-users"
                className="data-[state=active]:bg-[#D4AF37]/15 data-[state=active]:text-[#D4AF37] text-white/65 text-sm px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Users className="w-3.5 h-3.5" />
                All Users · {users.length}
              </TabsTrigger>
              <TabsTrigger
                value="broadcasts"
                data-testid="tab-broadcasts"
                className="data-[state=active]:bg-[#D4AF37]/15 data-[state=active]:text-[#D4AF37] text-white/65 text-sm px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Broadcasts
              </TabsTrigger>
              <TabsTrigger
                value="waitlist"
                data-testid="tab-waitlist"
                className="data-[state=active]:bg-[#D4AF37]/15 data-[state=active]:text-[#D4AF37] text-white/65 text-sm px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Users className="w-3.5 h-3.5" />
                Waitlist · {entries.length}
              </TabsTrigger>
              <TabsTrigger
                value="coupons"
                data-testid="tab-coupons"
                className="data-[state=active]:bg-[#D4AF37]/15 data-[state=active]:text-[#D4AF37] text-white/65 text-sm px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Coupons
              </TabsTrigger>
              <TabsTrigger
                value="coupon-usage"
                data-testid="tab-coupon-usage"
                className="data-[state=active]:bg-[#D4AF37]/15 data-[state=active]:text-[#D4AF37] text-white/65 text-sm px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Coupon Usage
              </TabsTrigger>
              <TabsTrigger
                value="cost"
                data-testid="tab-cost"
                className="data-[state=active]:bg-[#D4AF37]/15 data-[state=active]:text-[#D4AF37] text-white/65 text-sm px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <IndianRupee className="w-3.5 h-3.5" />
                Cost & Revenue
              </TabsTrigger>
              <TabsTrigger
                value="vendors"
                data-testid="tab-vendors"
                className="data-[state=active]:bg-[#D4AF37]/15 data-[state=active]:text-[#D4AF37] text-white/65 text-sm px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Users className="w-3.5 h-3.5" />
                Vendors
              </TabsTrigger>
              <TabsTrigger
                value="bookings"
                data-testid="tab-bookings"
                className="data-[state=active]:bg-[#D4AF37]/15 data-[state=active]:text-[#D4AF37] text-white/65 text-sm px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                Demo Bookings · {bookings.length}
              </TabsTrigger>
              <TabsTrigger
                value="leaks"
                data-testid="tab-leaks"
                className="data-[state=active]:bg-rose-400/15 data-[state=active]:text-rose-300 text-white/65 text-sm px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                Billing Leaks
              </TabsTrigger>
              <TabsTrigger
                value="audit"
                data-testid="tab-audit"
                className="data-[state=active]:bg-[#D4AF37]/15 data-[state=active]:text-[#D4AF37] text-white/65 text-sm px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <ScrollText className="w-3.5 h-3.5" />
                Audit Log
              </TabsTrigger>
            </TabsList>

            {tab !== "coupons" && tab !== "coupon-usage" && tab !== "cost" && tab !== "leaks" && tab !== "vendors" && tab !== "audit" && tab !== "broadcasts" && (
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search name, email, company"
                    data-testid="admin-search-input"
                    className="pl-9 pr-3 py-2.5 bg-[#141414] border border-white/10 rounded-lg text-sm w-64 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                  />
                </div>
                <button
                  onClick={loadAll}
                  data-testid="admin-refresh-btn"
                  className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg border border-white/10 hover:bg-white/5 transition"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </button>
                <button
                  onClick={() => exportCSV(
                    tab === "users" ? "users" : tab === "bookings" ? "bookings" : "waitlist",
                  )}
                  data-testid="admin-export-csv-btn"
                  className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg border border-[#D4AF37]/35 text-[#D4AF37] bg-[#D4AF37]/5 hover:bg-[#D4AF37]/15 transition"
                  title="Download visible rows as CSV"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </div>
            )}
          </div>

          <TabsContent value="waitlist" className="m-0">
            {/* KPI strip */}
            <div data-testid="admin-waitlist-kpis" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
              {(() => {
                const k = computeWaitlistKpis(entries);
                return (
                  <>
                    <KpiCard
                      testId="kpi-today"
                      title="Signups today"
                      value={k.today.value}
                      delta={k.today.delta}
                      label={k.today.label}
                      accent
                    />
                    <KpiCard
                      testId="kpi-week"
                      title="This week"
                      value={k.week.value}
                      delta={k.week.delta}
                      label={k.week.label}
                    />
                    <KpiCard
                      testId="kpi-month"
                      title="This month"
                      value={k.month.value}
                      delta={k.month.delta}
                      label={k.month.label}
                    />
                    <KpiCard
                      testId="kpi-all-time"
                      title="All time"
                      value={k.allTime.value}
                      delta={null}
                      label="total signups"
                    />
                  </>
                );
              })()}
            </div>

            <div className="glass-panel rounded-2xl overflow-hidden" data-testid="admin-waitlist-table">
              <div className="grid grid-cols-12 px-6 py-4 text-[10px] uppercase tracking-[0.22em] text-white/40 border-b border-white/5">
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-2">Company</div>
                <div className="col-span-2">Role</div>
                <div className="col-span-1">Submitted</div>
                <div className="col-span-1 text-right">—</div>
              </div>
              {loading ? (
                <div className="px-6 py-16 text-center text-white/45 text-sm">Loading submissions…</div>
              ) : filteredWaitlist.length === 0 ? (
                <div className="px-6 py-16 text-center text-white/45 text-sm">
                  {entries.length === 0 ? "No submissions yet." : "No matches for your search."}
                </div>
              ) : (
                filteredWaitlist.map((e, i) => (
                  <div
                    key={e.id || i}
                    data-testid={`admin-waitlist-row-${i}`}
                    className="grid grid-cols-12 px-6 py-4 text-sm border-b border-white/5 hover:bg-white/[0.02] transition"
                  >
                    <div className="col-span-3 font-medium text-white">{e.name}</div>
                    <div className="col-span-3 text-white/70 truncate flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-white/35" /> {e.email}
                    </div>
                    <div className="col-span-2 text-white/65 truncate flex items-center gap-1.5">
                      {e.company ? <><Building2 className="w-3.5 h-3.5 text-white/35" /> {e.company}</> : <span className="text-white/30">—</span>}
                    </div>
                    <div className="col-span-2 text-white/60 truncate">{e.role || <span className="text-white/30">—</span>}</div>
                    <div className="col-span-1 text-white/45 text-xs">
                      {e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button
                        onClick={() => setDeleteCtx({ kind: "waitlist", id: e.id, label: e.name })}
                        data-testid={`admin-delete-${i}`}
                        className="text-white/40 hover:text-red-400 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {filteredWaitlist.some((e) => e.message) && (
              <div className="mt-10">
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4">Recent notes</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredWaitlist.filter((e) => e.message).slice(0, 6).map((e) => (
                    <div key={e.id} className="glass-card rounded-2xl p-5">
                      <div className="text-sm text-white font-medium">{e.name}</div>
                      <div className="text-xs text-white/45 mb-3">{e.email}</div>
                      <p className="text-sm text-white/70 leading-relaxed italic font-display">"{e.message}"</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="users" className="m-0">
            {/* KPI strip — quick provider split + verification health. */}
            <div data-testid="admin-users-kpis" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
              <KpiCard
                title="Total users"
                value={users.length.toLocaleString()}
                label="all accounts"
                accent
                testId="admin-users-kpi-total"
              />
              <KpiCard
                title="Google sign-in"
                value={(usersByProvider.google || 0).toLocaleString()}
                label="via Google"
                testId="admin-users-kpi-google"
              />
              <KpiCard
                title="Password sign-up"
                value={(usersByProvider.password || 0).toLocaleString()}
                label="via waitlist"
                testId="admin-users-kpi-password"
              />
              <KpiCard
                title="Verified"
                value={users.filter((u) => u.email_verified).length.toLocaleString()}
                label="email confirmed"
                testId="admin-users-kpi-verified"
              />
            </div>

            {/* Provider filter chips — sit above the table for quick slicing. */}
            <div className="flex items-center gap-2 mb-3" data-testid="admin-users-filter-row">
              {[
                { v: "all", label: `All · ${users.length}` },
                { v: "google", label: `Google · ${usersByProvider.google || 0}` },
                { v: "password", label: `Password · ${usersByProvider.password || 0}` },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setProviderFilter(opt.v)}
                  data-testid={`admin-users-filter-${opt.v}`}
                  className={`text-[11px] px-3 py-1.5 rounded-full border transition ${
                    providerFilter === opt.v
                      ? "border-[#D4AF37]/55 bg-[#D4AF37]/10 text-[#D4AF37]"
                      : "border-white/10 bg-[#141414] text-white/65 hover:border-white/30 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="glass-panel rounded-2xl overflow-hidden" data-testid="admin-users-table">
              <div className="grid grid-cols-12 px-6 py-4 text-[10px] uppercase tracking-[0.22em] text-white/40 border-b border-white/5">
                <div className="col-span-4">Email · Name</div>
                <div className="col-span-2">Provider</div>
                <div className="col-span-1">Plan</div>
                <div className="col-span-1 text-right">Credits</div>
                <div className="col-span-3">Joined</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>
              {loading ? (
                <div className="px-6 py-16 text-center text-white/45 text-sm flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="px-6 py-16 text-center text-white/45 text-sm" data-testid="admin-users-empty">
                  No users match the current filter.
                </div>
              ) : (
                filteredUsers.map((u, i) => (
                  <div
                    key={u.user_id}
                    data-testid={`admin-users-row-${i}`}
                    className="grid grid-cols-12 px-6 py-3.5 items-center border-b border-white/5 hover:bg-white/[0.015] transition text-sm"
                  >
                    <div className="col-span-4 min-w-0">
                      <div className="flex items-center gap-2.5">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-7 h-7 rounded-full object-cover border border-white/10 flex-shrink-0" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-white/5 border border-white/10 flex-shrink-0 flex items-center justify-center text-[10px] text-white/45">
                            {(u.name || u.email || "?").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-white/90 truncate">{u.email}</div>
                          <div className="text-[11px] text-white/45 truncate">{u.name || <span className="italic text-white/30">no name</span>}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span
                        className={`inline-flex items-center gap-1.5 text-[10.5px] px-2 py-1 rounded-full border ${
                          u.provider === "google"
                            ? "border-blue-400/35 bg-blue-400/8 text-blue-200"
                            : "border-[#D4AF37]/35 bg-[#D4AF37]/8 text-[#D4AF37]"
                        }`}
                      >
                        {u.provider === "google" ? "Google" : "Password"}
                      </span>
                      {u.on_waitlist && (
                        <span className="ml-1.5 text-[10px] text-white/40">· waitlist</span>
                      )}
                    </div>
                    <div className="col-span-1 text-[12px] text-white/65 capitalize">{u.plan || "free"}</div>
                    <div className="col-span-1 text-right text-white/80 tabular-nums">{Math.round(u.credits || 0)}</div>
                    <div className="col-span-3 text-[11.5px] text-white/55">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                      {u.last_login && (
                        <div className="text-[10.5px] text-white/35">last seen {new Date(u.last_login).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</div>
                      )}
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button
                        onClick={() => setDeleteCtx({ kind: "user", id: u.user_id, label: u.email })}
                        data-testid={`admin-users-delete-${i}`}
                        title="Delete user (sessions + account)"
                        className="text-white/40 hover:text-red-400 transition p-1.5 rounded-md hover:bg-white/5"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>


          <TabsContent value="bookings" className="m-0">
            <div className="glass-panel rounded-2xl overflow-hidden" data-testid="admin-bookings-table">
              <div className="grid grid-cols-12 px-6 py-4 text-[10px] uppercase tracking-[0.22em] text-white/40 border-b border-white/5">
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-2">Date · Slot</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-3 text-right">Actions</div>
              </div>
              {loading ? (
                <div className="px-6 py-16 text-center text-white/45 text-sm">Loading bookings…</div>
              ) : filteredBookings.length === 0 ? (
                <div className="px-6 py-16 text-center text-white/45 text-sm">
                  {bookings.length === 0 ? "No demo bookings yet." : "No matches for your search."}
                </div>
              ) : (
                filteredBookings.map((b, i) => {
                  const status = (b.status || "pending").toLowerCase();
                  const pill = STATUS_PILL[status] || STATUS_PILL.pending;
                  const busy = statusBusyId === b.id;
                  return (
                    <div
                      key={b.id || i}
                      data-testid={`admin-booking-row-${i}`}
                      className="grid grid-cols-12 px-6 py-4 text-sm border-b border-white/5 hover:bg-white/[0.02] transition items-center"
                    >
                      <div className="col-span-3 font-medium text-white truncate">{b.name}</div>
                      <div className="col-span-3 text-white/70 truncate flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-white/35 flex-shrink-0" />
                        <span className="truncate">{b.email}</span>
                      </div>
                      <div className="col-span-2 text-white/75">
                        <div className="text-[#D4AF37]">{b.date}</div>
                        <div className="text-xs text-white/55">{b.time_slot}</div>
                      </div>
                      <div className="col-span-1">
                        <span
                          data-testid={`admin-booking-status-${i}`}
                          className={`inline-flex items-center text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${pill.cls}`}
                        >
                          {pill.label}
                        </span>
                      </div>
                      <div className="col-span-3 flex items-center justify-end gap-2">
                        <button
                          disabled={busy || status === "confirmed"}
                          onClick={() => updateBookingStatus(b, "confirmed")}
                          data-testid={`admin-booking-confirm-${i}`}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-emerald-400/40 text-emerald-300 bg-emerald-400/5 hover:bg-emerald-400/15 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Confirm — sends confirmation email"
                        >
                          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Confirm
                        </button>
                        <button
                          disabled={busy || status === "declined"}
                          onClick={() => updateBookingStatus(b, "declined")}
                          data-testid={`admin-booking-decline-${i}`}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-rose-400/40 text-rose-300 bg-rose-400/5 hover:bg-rose-400/15 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Decline — sends reschedule email"
                        >
                          <XIcon className="w-3 h-3" /> Decline
                        </button>
                        <button
                          onClick={() => setDeleteCtx({ kind: "booking", id: b.id, label: b.name })}
                          data-testid={`admin-booking-delete-${i}`}
                          className="text-white/40 hover:text-red-400 transition ml-1 p-1.5"
                          title="Delete booking"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {filteredBookings.some((b) => b.notes) && (
              <div className="mt-10">
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4">Booking notes</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredBookings.filter((b) => b.notes).slice(0, 6).map((b) => (
                    <div key={b.id} className="glass-card rounded-2xl p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-white font-medium">{b.name}</div>
                          <div className="text-xs text-white/45 mt-0.5">{b.email}</div>
                        </div>
                        <div className="text-xs text-[#D4AF37]">{b.date} · {b.time_slot}</div>
                      </div>
                      <p className="mt-3 text-sm text-white/70 leading-relaxed italic font-display">"{b.notes}"</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="coupons" className="m-0">
            <AdminCoupons />
          </TabsContent>

          <TabsContent value="cost" className="m-0">
            <AdminCostDashboard authHeader={authHeader} onUnauthorized={unauthorized} />
          </TabsContent>

          <TabsContent value="leaks" className="m-0">
            <AdminBillingLeaks authHeader={authHeader} onUnauthorized={unauthorized} />
          </TabsContent>

          <TabsContent value="vendors" className="m-0">
            <AdminVendors authHeader={authHeader} onUnauthorized={unauthorized} />
          </TabsContent>

          <TabsContent value="coupon-usage" className="m-0">
            <AdminCouponUsage authHeader={authHeader} onUnauthorized={unauthorized} />
          </TabsContent>

          <TabsContent value="audit" className="m-0">
            <AdminAuditLog onUnauthorized={unauthorized} />
          </TabsContent>

          <TabsContent value="broadcasts" className="m-0">
            <AdminBroadcasts authHeader={authHeader} onUnauthorized={unauthorized} />
          </TabsContent>
        </Tabs>
      </main>

      <LuxuryConfirmModal
        open={!!deleteCtx}
        onOpenChange={(v) => !v && setDeleteCtx(null)}
        title={deleteCtx?.kind === "booking" ? "Cancel this booking?" : "Delete this entry?"}
        description={deleteCtx ? `"${deleteCtx.label}" will be permanently removed. This cannot be undone.` : ""}
        confirmLabel={deleteCtx?.kind === "booking" ? "Cancel booking" : "Delete entry"}
        destructive
        onConfirm={confirmDelete}
      />
    </div>
  );
}
